import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useBrand } from 'hooks/useBrand'
import { toast } from 'react-toastify'
import {
  createInstantSession,
  finishInstantSession,
  getCallState,
  getDailyRoomUrl,
  getDailyToken,
  getoutQueue,
  getPaymentStatus,
  getSessionByIdForAssignment,
  normalizeSessionCall,
  notifyDoctors,
  payInstantSession,
  setSessionPathologies,
  startInstantSession,
  updateConnectionStatus
} from '@cannect/services/resources/instantConsultation/requests'
import {
  TCallStatePayload,
  TInstantConsultationStep,
  TPathology,
  TPaymentStatus,
  TSessionCall,
  TSessionPaymentPayload
} from '@cannect/services/resources/instantConsultation/types'

/** Resultado normalizado de um pagamento (response v1). */
export type TPaymentResult = {
  success: boolean
  status: TPaymentStatus
  qrCode: string | null
  qrCodeUrl: string | null
  transactionId: string | null
}

/** Decide em qual passo retomar a sessão (ver data-model.md → inferResumeStep). */
function inferResumeStep(session: TSessionCall): TInstantConsultationStep {
  if (session.status_payment === 'waiting_payment') {
    return 'payment'
  }
  const dailyRoomUrl = getDailyRoomUrl(session)
  const dailyToken = getDailyToken(session)
  if (session.precriber_id && dailyToken && dailyRoomUrl) {
    return 'video'
  }
  return 'queue'
}

const POLL_INTERVAL_MS = 5000

interface IInstantConsultationContext {
  step: TInstantConsultationStep
  session: TSessionCall | null
  selectedPathologies: TPathology[]
  questionnaireCompleted: boolean
  callDuration: number
  loading: boolean
  paymentError: string | null
  /** false ⇒ perfil incompleto (endereço/telefone) antes de pagar. */
  userCompleted: boolean
  goTo: (step: TInstantConsultationStep) => void
  goNext: () => void
  resetFlow: () => void
  setSelectedPathologies: (pathologies: TPathology[]) => void
  setQuestionnaireCompleted: (done: boolean) => void
  handleCreateSession: () => Promise<void>
  handlePayment: (payload: TSessionPaymentPayload) => Promise<TPaymentResult | null>
  pollPaymentStatus: () => Promise<TPaymentStatus>
  handleEnterQueue: () => Promise<void>
  handleLeaveQueue: () => Promise<void>
  handleFinishCall: (endedBy?: 'patient') => Promise<void>
  updatePatientConnectionStatus: (status: 'connected' | 'disconnected') => Promise<void>
  setSession: (session: TSessionCall) => void
  hydrateResumeSession: (session: TSessionCall) => void
}

const InstantConsultationContext = createContext<IInstantConsultationContext>({} as IInstantConsultationContext)

const STEPS_ORDER: TInstantConsultationStep[] = ['triage', 'questionnaire', 'payment', 'queue', 'video', 'conclusion']

export function InstantConsultationProvider({ children }: { children: ReactNode }) {
  const { brand, config } = useBrand()
  // Marca alias (ex.: Vikura) cria a sessão com a origem real (remederi) —
  // mesmo padrão do cadastro no agendamento branded.
  const brandedOrigin = config.scheduling.origin ?? (brand !== 'cannect' ? brand : null)
  const [step, setStep] = useState<TInstantConsultationStep>('triage')
  const [session, setSession] = useState<TSessionCall | null>(null)
  const [selectedPathologies, setSelectedPathologies] = useState<TPathology[]>([])
  const [questionnaireCompleted, setQuestionnaireCompleted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [paymentError, setPaymentError] = useState<string | null>(null)
  const [userCompleted, setUserCompleted] = useState(true)
  const [callDuration, setCallDuration] = useState(0)
  const [callStartTimestamp, setCallStartTimestamp] = useState<number | null>(null)

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const callStatePollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const paymentPollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const connectionReportedRef = useRef(false)

  const goTo = useCallback((target: TInstantConsultationStep) => {
    setStep(target)
  }, [])

  const goNext = useCallback(() => {
    setStep((prev) => {
      const idx = STEPS_ORDER.indexOf(prev)
      if (idx < STEPS_ORDER.length - 1) return STEPS_ORDER[idx + 1]
      return prev
    })
  }, [])

  const resetFlow = useCallback(() => {
    setStep('triage')
    setSession(null)
    setSelectedPathologies([])
    setQuestionnaireCompleted(false)
    setLoading(false)
    setPaymentError(null)
    setUserCompleted(true)
    setCallDuration(0)
    setCallStartTimestamp(null)
    connectionReportedRef.current = false

    if (pollingRef.current) clearInterval(pollingRef.current)
    if (callStatePollingRef.current) clearInterval(callStatePollingRef.current)
    if (paymentPollingRef.current) clearInterval(paymentPollingRef.current)
  }, [])

  const hydrateResumeSession = useCallback(
    (raw: TSessionCall) => {
      const resumed = normalizeSessionCall(raw)

      if (resumed.concluded_at) {
        toast.info('Esta consulta já foi encerrada.')
        resetFlow()
        return
      }

      setQuestionnaireCompleted(true)
      if (resumed.pathologies?.length) {
        const pathList = resumed.pathologies.map((p) => p.pathology).filter(Boolean) as TPathology[]
        if (pathList.length) {
          setSelectedPathologies(pathList)
        }
      }

      setSession(resumed)
      goTo(inferResumeStep(resumed))
    },
    [goTo, resetFlow]
  )

  const applyPathologiesToSession = useCallback(
    async (sessionId: number) => {
      if (selectedPathologies.length > 0) {
        await setSessionPathologies(
          sessionId,
          selectedPathologies.map((p) => p.id)
        )
      }
    },
    [selectedPathologies]
  )

  const handleCreateSession = useCallback(async () => {
    if (session) {
      await applyPathologiesToSession(session.id)
      return
    }
    setLoading(true)
    try {
      const { session: newSession, user_completed } = await createInstantSession(brandedOrigin)
      setUserCompleted(user_completed !== false)
      const normalized = normalizeSessionCall(newSession)
      setSession(normalized)
      await applyPathologiesToSession(normalized.id)
    } catch (error) {
      toast.error('Erro ao preparar sessão. Tente novamente.')
      throw error
    } finally {
      setLoading(false)
    }
  }, [session, applyPathologiesToSession, brandedOrigin])

  const handlePayment = useCallback(
    async (payload: TSessionPaymentPayload): Promise<TPaymentResult | null> => {
      if (!session) return null
      setLoading(true)
      setPaymentError(null)
      try {
        const response = await payInstantSession(payload)
        const { status } = response
        if (status) {
          setSession((prev) => (prev ? { ...prev, status_payment: status } : prev))
        }
        if (status === 'refused' || status === 'failed') {
          const msg = 'Pagamento não aprovado. Verifique os dados e tente novamente.'
          setPaymentError(msg)
        }
        return {
          success: Boolean(response.success),
          status,
          qrCode: response.qr_code ?? null,
          qrCodeUrl: response.qr_code_url ?? null,
          transactionId: response.transaction_id ?? null
        }
      } catch (error: any) {
        // Recusa do adquirente chega como 400 { success:false, message }.
        const msg = error?.response?.data?.message || 'Erro ao processar pagamento.'
        setPaymentError(msg)
        toast.error(msg)
        return null
      } finally {
        setLoading(false)
      }
    },
    [session]
  )

  // Polling de confirmação de pagamento (PIX/aguardando) até estado terminal.
  const pollPaymentStatus = useCallback(() => {
    return new Promise<TPaymentStatus>((resolve) => {
      if (!session?.id) {
        resolve('failed')
        return
      }
      const sessionId = session.id

      if (paymentPollingRef.current) clearInterval(paymentPollingRef.current)

      const check = async () => {
        try {
          const { status } = await getPaymentStatus(sessionId)
          if (status === 'paid' || status === 'refused' || status === 'failed') {
            if (paymentPollingRef.current) {
              clearInterval(paymentPollingRef.current)
              paymentPollingRef.current = null
            }
            setSession((prev) => (prev ? { ...prev, status_payment: status } : prev))
            resolve(status)
          }
        } catch {
          // noop - mantém o polling
        }
      }

      check()
      paymentPollingRef.current = setInterval(check, POLL_INTERVAL_MS)
    })
  }, [session?.id])

  const handleEnterQueue = useCallback(async () => {
    if (!session) return
    setLoading(true)
    try {
      await startInstantSession(session.uuid)
      goTo('queue')
      // Notifica os médicos credenciados (US3 / FR-013); best-effort.
      try {
        await notifyDoctors(session.uuid)
      } catch {
        // noop - notificação é best-effort, não bloqueia a fila
      }
    } catch (error) {
      toast.error('Erro ao entrar na fila. Tente novamente.')
      throw error
    } finally {
      setLoading(false)
    }
  }, [session, goTo])

  const handleLeaveQueue = useCallback(async () => {
    if (!session?.uuid) return
    setLoading(true)
    try {
      await getoutQueue(session.uuid)
      toast.info('Você saiu da fila.')
    } catch {
      toast.error('Não foi possível sair da fila. Tente novamente.')
    } finally {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
        pollingRef.current = null
      }
      setLoading(false)
      resetFlow()
    }
  }, [session?.uuid, resetFlow])

  const updatePatientConnectionStatus = useCallback(
    async (status: 'connected' | 'disconnected') => {
      if (!session?.uuid) return
      try {
        const response = await updateConnectionStatus(session.uuid, { role: 'patient', status })
        const callState: TCallStatePayload = response.call_state
        if (callState?.call_started_at) {
          setCallStartTimestamp(new Date(callState.call_started_at).getTime())
        }
        connectionReportedRef.current = status === 'connected'
      } catch {
        // noop - conexao reportada na base de melhor esforco
      }
    },
    [session?.uuid]
  )

  const handleFinishCall = useCallback(
    async (endedBy: 'patient' = 'patient') => {
      if (!session?.id) return

      if (callStatePollingRef.current) {
        clearInterval(callStatePollingRef.current)
        callStatePollingRef.current = null
      }

      try {
        if (connectionReportedRef.current) {
          await updatePatientConnectionStatus('disconnected')
        }
        await finishInstantSession(session.id, endedBy)
      } catch {
        // noop
      } finally {
        connectionReportedRef.current = false
        goTo('conclusion')
      }
    },
    [session?.id, goTo, updatePatientConnectionStatus]
  )

  // Polling durante fila de espera - aguarda atribuicao do medico (campos Daily).
  useEffect(() => {
    if (step !== 'queue' || !session?.id) {
      return undefined
    }

    const sessionId = session.id

    const poll = async () => {
      try {
        const assignment = await getSessionByIdForAssignment(sessionId)
        const dailyRoomUrl = getDailyRoomUrl(assignment)
        const dailyToken = getDailyToken(assignment)

        if (assignment.precriber_id && dailyToken && dailyRoomUrl) {
          if (pollingRef.current) {
            clearInterval(pollingRef.current)
            pollingRef.current = null
          }
          setSession((prev) =>
            prev
              ? {
                  ...prev,
                  precriber_id: assignment.precriber_id,
                  prescriber_name: assignment.prescriber_name ?? prev.prescriber_name,
                  status_payment: assignment.status_payment ?? prev.status_payment,
                  dailyRoomUrl,
                  dailyMeetingToken: dailyToken
                }
              : prev
          )
          goTo('video')
        }
      } catch {
        // noop
      }
    }

    poll()
    pollingRef.current = setInterval(poll, POLL_INTERVAL_MS)

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current)
    }
  }, [step, session?.id, goTo])

  // Polling do estado da chamada durante a videochamada (detecta encerramento pelo medico)
  useEffect(() => {
    if (step !== 'video' || !session?.uuid) {
      return undefined
    }

    const fetchState = async () => {
      try {
        const response = await getCallState(session.uuid)
        const callState: TCallStatePayload = response.call_state
        if (callState?.call_started_at && !callStartTimestamp) {
          setCallStartTimestamp(new Date(callState.call_started_at).getTime())
        }
        if (callState?.concluded_at && callState.call_finished_by && callState.call_finished_by !== 'patient') {
          toast.info('O médico encerrou a consulta.')
          await handleFinishCall()
        }
      } catch {
        // noop
      }
    }

    callStatePollingRef.current = setInterval(fetchState, POLL_INTERVAL_MS)

    return () => {
      if (callStatePollingRef.current) clearInterval(callStatePollingRef.current)
    }
  }, [step, session?.uuid])

  // Timer de duração da chamada
  useEffect(() => {
    if (!callStartTimestamp) {
      setCallDuration(0)
      return undefined
    }

    const tick = () => {
      setCallDuration(Math.max(0, Math.floor((Date.now() - callStartTimestamp) / 1000)))
    }

    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [callStartTimestamp])

  const contextValue = useMemo(
    () => ({
      step,
      session,
      selectedPathologies,
      questionnaireCompleted,
      callDuration,
      loading,
      paymentError,
      userCompleted,
      goTo,
      goNext,
      resetFlow,
      setSelectedPathologies,
      setQuestionnaireCompleted,
      handleCreateSession,
      handlePayment,
      pollPaymentStatus,
      handleEnterQueue,
      handleLeaveQueue,
      handleFinishCall,
      updatePatientConnectionStatus,
      setSession,
      hydrateResumeSession
    }),
    [
      step,
      session,
      selectedPathologies,
      questionnaireCompleted,
      callDuration,
      loading,
      paymentError,
      userCompleted,
      goTo,
      goNext,
      resetFlow,
      handleCreateSession,
      handlePayment,
      pollPaymentStatus,
      handleEnterQueue,
      handleLeaveQueue,
      handleFinishCall,
      updatePatientConnectionStatus,
      hydrateResumeSession
    ]
  )

  return <InstantConsultationContext.Provider value={contextValue}>{children}</InstantConsultationContext.Provider>
}

export function useInstantConsultation(): IInstantConsultationContext {
  const context = useContext(InstantConsultationContext)
  if (!context) {
    throw new Error('useInstantConsultation must be used within InstantConsultationProvider')
  }
  return context
}
